import { Component, type ComponentType, type ReactNode } from "react";

/**
 * FIXED BD-6 (H25): per-block error boundary for the published guest site.
 *
 * Before this, the ONLY boundary was the app-wide Sentry boundary in main.tsx,
 * so one corrupt block prop (e.g. a footer link-group missing its `links`
 * array → `links.filter` throws during render) replaced the ENTIRE published
 * site with the AppCrash fallback. Every runtime block is now wrapped in its
 * own boundary (see resolver.ts): a single bad node degrades to nothing while
 * the rest of the page renders normally.
 */
interface BoundaryProps {
  children?: ReactNode;
}
interface BoundaryState {
  hasError: boolean;
}

class BlockErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    // Log for Sentry/console, but never re-throw — a single block failing
    // must not take the whole guest site down.
    // eslint-disable-next-line no-console
    console.error("[wb-runtime] block render failed, isolated by boundary", error);
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Wrap a resolver component so a render throw in it (or an unguarded child that
 * bubbles to it) is caught locally. Preserves any `craft` static the builder
 * engine reads at deserialize time, and forwards all props + children.
 */
export function withBlockBoundary<P extends Record<string, unknown>>(
  Inner: ComponentType<P>,
): ComponentType<P> {
  const Wrapped = (props: P) => (
    <BlockErrorBoundary>
      <Inner {...props} />
    </BlockErrorBoundary>
  );
  Wrapped.displayName = `BlockBoundary(${Inner.displayName || Inner.name || "Component"})`;
  const craft = (Inner as unknown as { craft?: unknown }).craft;
  if (craft !== undefined) {
    (Wrapped as unknown as { craft?: unknown }).craft = craft;
  }
  return Wrapped as ComponentType<P>;
}
